import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { getRoomName } from '../../../base/conference/functions';
import i18next from '../../../base/i18n/i18next';
import Icon from '../../../base/icons/components/Icon';
import { IconVolumeOff, IconVolumeUp } from '../../../base/icons/svg';
import { audioAutoplayUnblocked, isAudioAutoplayBlocked, resumeRoomAudio } from '../../../base/media/autoplay';
import { emitMobileTelemetryEvent } from '../../../mobile/navigation/telemetry';
/**
 * "Turn on sound": shown while the browser refuses to start the call's audio
 * (base/media/autoplay.ts), so a blocked room is never silently mute.
 *
 * - The click plays every call-audio element inside the person's gesture and
 *   hides the prompt only when all of them play. If the browser still
 *   refuses, the prompt stays and says so.
 * - Any other press or key anywhere in the room is also a gesture: while the
 *   prompt is up, the first one resumes the room's audio too, so a person
 *   who never looks at the prompt is not left mute.
 * - "Not now" hides it without pretending the sound is on (a Deaf caller may
 *   not want it at all) and stops the gesture resume. A small "Sound off"
 *   control stays, and brings the prompt back.
 * - Screen readers hear the block: the live region is mounted empty from the
 *   start and filled when the block happens (a region inserted together with
 *   its text is often not announced).
 */
function tr(key) {
    return String(i18next.t(key));
}
// One report per page: every remote audio element fails at about the same
// moment, and a reload is a new page with its own chance to be blocked.
let blockReported = false;
/** Test seam: a fresh page. */
export function resetAudioAutoplayPromptForTests() {
    blockReported = false;
}
// A press or key on these (the prompt's own buttons, the Sound off control)
// is answered by that control, not by the room-wide gesture resume.
const CONTROL_ATTRIBUTE = 'data-audio-autoplay-control';
// Gestures that grant the page user activation: a mouse press or key down,
// and the release of a touch or pen (a touch press alone grants none).
const GESTURE_EVENTS = ['pointerdown', 'pointerup', 'keydown'];
function isAutoplayControl(target) {
    const element = target;
    return typeof element?.closest === 'function' && Boolean(element.closest(`[${CONTROL_ATTRIBUTE}]`));
}
const useStyles = makeStyles()(theme => ({
    liveRegion: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        whiteSpace: 'nowrap',
        width: 1
    },
    prompt: {
        alignItems: 'center',
        background: 'rgba(8, 12, 20, 0.92)',
        border: '1px solid rgba(255, 255, 255, 0.18)',
        borderRadius: 14,
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.35)',
        color: '#F5F8FB',
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1.25),
        justifyContent: 'center',
        left: '50%',
        maxWidth: 'min(520px, calc(100% - 24px))',
        padding: theme.spacing(1.25, 1.5),
        position: 'absolute',
        top: theme.spacing(1.5),
        transform: 'translateX(-50%)',
        zIndex: 9
    },
    turnOn: {
        alignItems: 'center',
        background: '#FFFFFF',
        border: 0,
        borderRadius: 999,
        color: '#0B1220',
        cursor: 'pointer',
        display: 'inline-flex',
        font: 'inherit',
        fontSize: 16,
        fontWeight: 800,
        gap: theme.spacing(0.75),
        minHeight: 48,
        padding: theme.spacing(1, 2.25),
        '&:disabled': {
            cursor: 'progress',
            opacity: 0.7
        },
        '&:focus-visible': {
            outline: '3px solid #8CC8FF',
            outlineOffset: 2
        }
    },
    icon: {
        display: 'inline-flex'
    },
    message: {
        fontSize: 14,
        lineHeight: 1.4,
        margin: 0
    },
    soundOff: {
        alignItems: 'center',
        background: 'rgba(8, 12, 20, 0.82)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        borderRadius: 999,
        color: '#F5F8FB',
        cursor: 'pointer',
        display: 'inline-flex',
        font: 'inherit',
        fontSize: 13,
        fontWeight: 600,
        gap: theme.spacing(0.5),
        left: '50%',
        minHeight: 44,
        padding: theme.spacing(0.5, 1.25),
        position: 'absolute',
        top: theme.spacing(1.5),
        transform: 'translateX(-50%)',
        zIndex: 9,
        '&:focus-visible': {
            outline: '3px solid #8CC8FF',
            outlineOffset: 2
        }
    },
    notNow: {
        background: 'transparent',
        border: '1px solid rgba(255, 255, 255, 0.35)',
        borderRadius: 999,
        color: '#F5F8FB',
        cursor: 'pointer',
        font: 'inherit',
        fontSize: 14,
        minHeight: 44,
        padding: theme.spacing(0.75, 1.5),
        '&:focus-visible': {
            outline: '3px solid #8CC8FF',
            outlineOffset: 2
        }
    }
}));
const AudioAutoplayPrompt = ({ role }) => {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const blocked = useSelector((state) => isAudioAutoplayBlocked(state));
    const roomName = useSelector((state) => getRoomName(state));
    const [status, setStatus] = useState('idle');
    const [dismissed, setDismissed] = useState(false);
    useEffect(() => {
        if (!blocked || blockReported) {
            return;
        }
        blockReported = true;
        emitMobileTelemetryEvent('vrs_room_audio_autoplay_blocked', { role }, { roomName });
    }, [blocked, role, roomName]);
    // The room-wide resume: while the prompt is up, the next press or key
    // anywhere else plays the room's audio inside that gesture. Listening in
    // the capture phase, so nothing in the room can swallow it first.
    useEffect(() => {
        if (!blocked || dismissed
            || typeof document === 'undefined' || typeof document.addEventListener !== 'function') {
            return undefined;
        }
        let attempting = false;
        let failureReported = false;
        const onGesture = (event) => {
            if (attempting || isAutoplayControl(event.target)) {
                return;
            }
            attempting = true;
            // Synchronous inside the gesture: every play() carries it.
            resumeRoomAudio(document).then(result => {
                attempting = false;
                if (result.ok || !failureReported) {
                    failureReported = failureReported || !result.ok;
                    emitMobileTelemetryEvent('vrs_room_audio_autoplay_resume', {
                        elements: result.played,
                        error: result.error,
                        ok: result.ok,
                        role,
                        trigger: 'gesture'
                    }, { roomName });
                }
                if (result.ok) {
                    dispatch(audioAutoplayUnblocked());
                }
            });
        };
        GESTURE_EVENTS.forEach(type => document.addEventListener(type, onGesture, true));
        return () => GESTURE_EVENTS.forEach(type => document.removeEventListener(type, onGesture, true));
    }, [blocked, dismissed, dispatch, role, roomName]);
    const message = tr(status === 'failed' ? 'vrs.layout.sound.stillBlocked' : 'vrs.layout.sound.blocked');
    // Mounted before any block, so filling it is announced.
    const liveRegion = (React.createElement("div", { "aria-live": 'polite', className: classes.liveRegion, "data-testid": 'vrs-audio-autoplay-announcement', role: 'status' }, blocked && !dismissed ? message : ''));
    if (!blocked) {
        return liveRegion;
    }
    if (dismissed) {
        return (React.createElement(React.Fragment, null,
            liveRegion,
            React.createElement("button", { "aria-label": tr('vrs.layout.sound.soundOffLabel'), className: classes.soundOff, "data-audio-autoplay-control": 'true', "data-testid": 'vrs-audio-sound-off', onClick: () => {
                    setStatus('idle');
                    setDismissed(false);
                }, type: 'button' },
                React.createElement("span", { "aria-hidden": 'true', className: classes.icon },
                    React.createElement(Icon, { color: '#F5F8FB', size: 16, src: IconVolumeOff })),
                tr('vrs.layout.sound.soundOff'))));
    }
    const onTurnOn = () => {
        setStatus('pending');
        // Synchronous inside the click: every play() carries the gesture.
        resumeRoomAudio(document).then(result => {
            emitMobileTelemetryEvent('vrs_room_audio_autoplay_resume', {
                elements: result.played,
                error: result.error,
                ok: result.ok,
                role,
                trigger: 'button'
            }, { roomName });
            if (result.ok) {
                setStatus('idle');
                dispatch(audioAutoplayUnblocked());
            }
            else {
                setStatus('failed');
            }
        });
    };
    return (React.createElement(React.Fragment, null,
        liveRegion,
        React.createElement("div", { className: classes.prompt, "data-audio-autoplay-control": 'true', "data-testid": 'vrs-audio-autoplay-prompt' },
            React.createElement("button", { className: classes.turnOn, disabled: status === 'pending', onClick: onTurnOn, type: 'button' },
                React.createElement("span", { "aria-hidden": 'true', className: classes.icon },
                    React.createElement(Icon, { color: '#0B1220', size: 20, src: IconVolumeUp })),
                tr('vrs.layout.sound.turnOn')),
            React.createElement("p", { "aria-hidden": 'true', className: classes.message }, message),
            React.createElement("button", { className: classes.notNow, onClick: () => setDismissed(true), type: 'button' }, tr('vrs.layout.sound.notNow')))));
};
export default AudioAutoplayPrompt;
